<template>
    <div class="grid">
        <div class="unit half">
            <h1>Library</h1>
            <p class="lead">Browse the full range of books.</p>
        </div>
        <div v-if="!isMultiAssignEnabled" class="unit half">
            <input
                v-model="searchValue"
                class="libsearch old-libsearch pull-right"
                type="text"
                name="libsearch"
                placeholder="Search"
                @input="onSearchInput"
            />
        </div>
    </div>
    <div class="grid">
        <div class="unit one-quarter">
            <TeacherLibraryFilters
                ref="libraryFiltersRef"
                @quick-filter="quickFilter"
                @sorted="
                    search = '';
                    searchValue = '';
                    resetQuickFilters();
                "
            />
        </div>
        <div class="unit three-quarters">
            <div
                v-if="isMultiAssignEnabled"
                style="margin-bottom: 1rem"
                class="d-flex justify-space-between align-center"
            >
                <div>
                    <a
                        class="btn btn-small mr-3"
                        href="#"
                        :class="{ 'bg-secondary': multiSelect }"
                        @click="
                            multiSelect = !multiSelect;
                            selectedBooks = [];
                        "
                        >Choose books</a
                    >
                    <a
                        v-if="multiSelect"
                        class="btn btn-small mr-3"
                        :class="{ disabled: selectedBooks.length == 0 }"
                        style="pointer-events: auto"
                        href="#"
                        @click="manageAccess"
                        >Manage access
                        <v-tooltip
                            v-if="selectedBooks.length == 0"
                            activator="parent"
                            location="top"
                            text="Select at least one book"
                        ></v-tooltip>
                    </a>
                    <a
                        v-if="multiSelect"
                        class="btn btn-small mr-3"
                        :class="{ disabled: !hasReadingLevelFilter }"
                        style="pointer-events: auto"
                        href="#"
                        @click="selectAll"
                        >Select all
                        <v-tooltip
                            v-if="!hasReadingLevelFilter"
                            activator="parent"
                            location="top"
                            text="Please filter by level to enable Select all"
                        ></v-tooltip>
                    </a>
                    <a
                        v-if="multiSelect"
                        class="btn btn-small"
                        href="#"
                        @click="
                            multiSelect = false;
                            selectedBooks = [];
                        "
                        >Cancel</a
                    >
                </div>
                <div>
                    <input
                        v-model="searchValue"
                        class="libsearch"
                        type="text"
                        name="libsearch"
                        placeholder="Search"
                        @input="onSearchInput"
                    />
                </div>
            </div>
            <div id="results_panel" class="panel">
                <v-progress-circular v-if="isLoading" color="primary" indeterminate></v-progress-circular>
                <h2 v-else>
                    Found {{ total }} book{{ total == 1 ? '' : 's' }}
                    {{ selectedBooks.length > 0 ? ` - ${selectedBooks.length} selected` : '' }}
                </h2>
                <v-alert v-if="error" type="info" class="mx-2">
                    {{ error }}
                </v-alert>
                <div id="book_list" class="grid no-gutters carousel">
                    <template v-for="(book, i) of searchResult" :key="book.id">
                        <div class="cursor-pointer" @click="selectBook($event, book)">
                            <div class="unit one-third">
                                <v-lazy>
                                    <TeacherBookCard
                                        :book="book"
                                        :selected="selectedBooks.some((b) => b.bookId === book.bookId)"
                                        :mode="multiSelect ? 'selection' : 'normal'"
                                    />
                                </v-lazy>
                            </div>
                        </div>
                        <br v-if="i > 0 && i % 3 == 2" style="clear: both" />
                    </template>
                    <div v-if="isLoading" style="clear: both">Loading...</div>
                    <div v-else-if="hasMore" style="cursor: pointer; clear: both" @click="load()">Load more</div>
                    <div v-else-if="total === 0" style="clear: both">
                        <p v-if="filters.filter((f) => f.includes('favourites')).length">
                            Look out for the heart icon to save books to your favourites.
                        </p>
                        <p v-else-if="filters.filter((f) => f.includes('assigned')).length">No assigned books found</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <TeacherManageAccessDialog
        v-model="manageAccessDialog"
        :books="selectedBooks"
        @remove="removeBook"
        @done="
            multiSelect = false;
            selectedBooks = [];
        "
    />
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { GigletsBookV1 } from '@ilteducation/content-registry-types';

definePage({ meta: { area: 'books' } });
const router = useRouter();

const store = useTeacherStore();
const school = await store.fetchSchool();
const { favourites } = storeToRefs(useFavouritesStore());
const assignedBooksStore = useAssignedBooks();
assignedBooksStore.fetchAssignedBooks('teacher', []);
const { assignedBooks } = storeToRefs(assignedBooksStore);

const multiSelect = ref(false);
const selectedBooks = ref<GigletsBookV1[]>([]);
const manageAccessDialog = ref(false);
const error = ref<string | null>(null);

const { isMultiAssignEnabled } = useEnv();

// Load more books if the screen is bigger
const limit = Math.max(9, (Math.round(window.innerHeight / 500) + 1) * 3);

const { searchResult, isLoading, hasMore, total, load, loadAll, hasReadingLevelFilter, search, sort, filters } =
    useTeacherLibrary(limit, school.value);
const searchValue = ref('');

watch(
    search,
    (s) => {
        if (searchValue.value === '') {
            searchValue.value = s;
        }
    },
    { immediate: true },
);

let searchDebounceTimer: NodeJS.Timeout;
function onSearchInput(evt: Event) {
    const s = (evt.target as HTMLInputElement).value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }
    searchDebounceTimer = setTimeout(
        () => {
            search.value = searchValue.value;
            if (s === '') {
                sort.value = 'popularity:desc';
            } else {
                resetQuickFilters();
                sort.value = '';
            }
        },
        s === '' ? 0 : 350,
    );
}

const handleInfiniteScroll = () => {
    const bookListElement = document.getElementById('book_list');
    const rect = bookListElement?.getBoundingClientRect();
    if (!rect) {
        return;
    }
    const endOfPage = rect.bottom - 500 < window.innerHeight;
    if (endOfPage && hasMore.value) {
        load();
    }
};

window.addEventListener('scroll', handleInfiniteScroll);

function selectBook(evt: MouseEvent, book: GigletsBookV1) {
    if (!multiSelect.value) {
        router.push(`/teacher/book/${book.bookId}/about`);
        return;
    }
    evt.preventDefault();
    evt.stopPropagation();
    if (selectedBooks.value.some((b) => b.bookId === book.bookId)) {
        removeBook(book);
    } else {
        selectedBooks.value.push(book);
    }
}
async function selectAll() {
    if (hasReadingLevelFilter.value) {
        await loadAll();
        selectedBooks.value = [...new Set([...selectedBooks.value, ...searchResult.value])];
    }
}
function removeBook(book: GigletsBookV1) {
    selectedBooks.value = selectedBooks.value.filter((b) => b.bookId !== book.bookId);
}
function manageAccess() {
    if (selectedBooks.value.length === 0) {
        return;
    }
    manageAccessDialog.value = true;
}
function quickFilter(filter: string) {
    search.value = '';
    searchValue.value = '';
    let newFilters = filters.value.filter((filter) => !filter.includes('bookId'));
    switch (filter) {
        case 'favourites':
            newFilters = newFilters.concat(
                favourites.value.map((f) => `bookId:${f}`),
                'bookId:favourites',
            );
            break;
        case 'assigned': {
            const uniqueAssigned = Array.from(new Set(assignedBooks.value.map((a) => a.book_id)));
            if (uniqueAssigned.length > 400) {
                error.value = 'You have too many assigned books to show all of them.';
            }
            newFilters = newFilters.concat(
                uniqueAssigned.slice(0, 400).flatMap((b) => `bookId:${b}`),
                'bookId:assigned',
            );
            break;
        }
        default:
            break;
    }
    filters.value = newFilters;
}

const libraryFiltersRef = ref<any>(null);

const resetQuickFilters = () => {
    libraryFiltersRef.value?.resetQuickFilters();
    filters.value = filters.value.filter((filter) => !filter.includes('bookId'));
};

onBeforeRouteLeave(() => {
    resetQuickFilters();
});
</script>

<style scoped lang="scss">
.libsearch {
    background-color: #f9fbfc;
    border: 1px solid #555;
    padding: 1px 5px;
    margin: 0;
}

.old-libsearch {
    margin-top: 60px;
}
</style>
